/**
 * SEO project URL segments:
 *   /premiumprojects/the-cascades
 *   /premiumprojects/the-cascades-2-3-4bhk-flats-in-neopolis
 *   /categoryprojects/...  /allprojects/...
 *
 * Property-type words: apartments → flats, villa → villas, plot → plots.
 */

import { decodeRouteSlug, normalizeProjectSlugForApi, pickProjectSlugFromItem } from './projectSlug';
import {
  pickBhkFromProject,
  pickLocationFromProject,
  unwrapProjectRecord,
} from './recommendationCard';
import { isPlaceholder } from './placeholderCheck';
import { resolveLocalityAndCityFromProject } from './pdpSearchContext';

const TYPE_WORD_BY_PROPERTY = {
  apartment: 'flats',
  villa: 'villas',
  plot: 'plots',
};

const TYPE_WORDS = Object.values(TYPE_WORD_BY_PROPERTY);

export function getPropertyTypeUrlWord(propertyType = 'apartment') {
  const t = String(propertyType || 'apartment').toLowerCase();
  if (t === 'villa') return TYPE_WORD_BY_PROPERTY.villa;
  if (t === 'plot') return TYPE_WORD_BY_PROPERTY.plot;
  return TYPE_WORD_BY_PROPERTY.apartment;
}

/** `Neopolis, Hyd` / `Hyderabad, Neopolis` → `neopolis` (empty for N/A placeholders) */
export function slugifyLocationSegment(location) {
  const raw = String(location ?? '').trim();
  if (!raw || isPlaceholder(raw)) return '';

  if (!raw.includes(',')) {
    return normalizeProjectSlugForApi(raw);
  }

  const parts = raw.split(',').map((p) => p.trim()).filter(Boolean);
  if (!parts.length) return '';
  if (parts.length === 1) {
    return isPlaceholder(parts[0]) ? '' : normalizeProjectSlugForApi(parts[0]);
  }

  const first = parts[0];
  const last = parts[parts.length - 1];

  if (last.length <= 4 && first.length > last.length) {
    return isPlaceholder(first) ? '' : normalizeProjectSlugForApi(first);
  }

  return isPlaceholder(last) ? '' : normalizeProjectSlugForApi(last);
}

function resolveLocationLabelFromItem(item) {
  if (!item || typeof item !== 'object') return '';

  const p = unwrapProjectRecord(item);
  const raw = item?.raw && typeof item.raw === 'object' ? item.raw : p;
  const title = String(p?.title || item?.title || raw?.title || '').trim();
  const { locationFromProject } = resolveLocalityAndCityFromProject(
    raw?.details || item?.details ? raw : p,
    title,
  );
  if (locationFromProject && !isPlaceholder(locationFromProject)) {
    return locationFromProject;
  }

  const d = raw?.details || item?.details || p?.details || {};

  const label =
    pickLocationFromProject(p) ||
    pickLocationFromProject(raw) ||
    raw?.location ||
    item?.location ||
    raw?.area ||
    item?.area ||
    raw?.locality ||
    item?.locality ||
    d?.location ||
    d?.area ||
    d?.locality ||
    d?.location_name ||
    '';

  const trimmed = String(label ?? '').trim();
  return isPlaceholder(trimmed) ? '' : trimmed;
}

function readDetailPairValue(items, titleNeedle) {
  if (!Array.isArray(items)) return '';
  const needle = String(titleNeedle || '').toLowerCase();
  const row = items.find((item) =>
    String(item?.title || item?.key || '').toLowerCase().includes(needle),
  );
  return String(row?.value ?? '').trim();
}

function uniqueBhkLabelFromConfigurationTypes(configs) {
  if (!Array.isArray(configs) || !configs.length) return '';
  const nums = [
    ...new Set(
      configs
        .map((c) => String(c?.configuration_type || c?.type || '').trim())
        .filter(Boolean)
        .map((t) => t.replace(/\s*bhk\s*/gi, '').trim())
        .filter(Boolean),
    ),
  ];
  if (!nums.length) return '';
  return `${nums.join(', ')} BHK`;
}

function resolveBhkFromProjectData(projectData, propertyType = 'apartment') {
  if (propertyType === 'plot') return '';

  const fromConfigArray = uniqueBhkLabelFromConfigurationTypes(projectData?.configurations);
  if (fromConfigArray) return fromConfigArray;

  const proj = projectData?.project || projectData || {};
  const fromNestedConfigs = uniqueBhkLabelFromConfigurationTypes(proj?.configurations);
  if (fromNestedConfigs) return fromNestedConfigs;

  const fromDetails =
    readDetailPairValue(projectData?.details, 'configuration') ||
    readDetailPairValue(projectData?.project_info, 'configuration') ||
    readDetailPairValue(projectData?.details, 'configurations') ||
    readDetailPairValue(projectData?.project_info, 'configurations');
  if (fromDetails) return fromDetails;

  const fromItem =
    resolveBhkLabelFromItem(proj, propertyType) ||
    resolveBhkLabelFromItem(projectData, propertyType);
  if (fromItem) return fromItem;

  return '';
}

function resolveBhkLabelFromItem(item, propertyType = 'apartment') {
  if (!item || typeof item !== 'object') return '';
  if (propertyType === 'plot') return '';

  const p = unwrapProjectRecord(item);
  const raw = item?.raw && typeof item.raw === 'object' ? item.raw : p;
  const d = raw?.details || item?.details || p?.details || {};
  const pageType = propertyType === 'villa' ? 'villa' : 'apartment';

  const fromPick =
    pickBhkFromProject(p, pageType) || pickBhkFromProject(raw, pageType);
  if (fromPick) return fromPick;

  const filtersBhk = item?.filters?.bhk ?? raw?.filters?.bhk;
  if (Array.isArray(filtersBhk) && filtersBhk.length) {
    return `${filtersBhk.join(', ')} BHK`;
  }

  if (Array.isArray(d?.configurations)) {
    const fromArray = uniqueBhkLabelFromConfigurationTypes(d.configurations);
    if (fromArray) return fromArray;
  }
  if (Array.isArray(item?.configurations)) {
    const fromArray = uniqueBhkLabelFromConfigurationTypes(item.configurations);
    if (fromArray) return fromArray;
  }

  return String(
    item?.flat ??
      raw?.flat ??
      raw?.bhk ??
      raw?.bhk_size ??
      raw?.configurations ??
      raw?.configuration ??
      item?.bhk ??
      item?.bhk_size ??
      item?.configurations ??
      item?.configuration ??
      d?.configurations ??
      d?.bhk ??
      d?.bhk_configuration ??
      '',
  ).trim();
}

/** `2, 3, 4 BHK` / `2.5, 3 BHK` → `2-3-4bhk` / `2-5-3bhk` */
export function formatBhkForUrlSegment(bhkRaw) {
  const s = String(bhkRaw ?? '').trim();
  if (!s) return '';
  const nums = s.replace(/\bbhk\b/gi, '').match(/\d+(?:\.\d+)?/g);
  if (!nums?.length) return '';
  const parts = [...new Set(nums.map((n) => n.replace(/\./g, '-')))]
    .map((n) => n.replace(/^0+(\d)/, '$1'));
  return `${parts.join('-')}bhk`;
}

function isValidBhkUrlTail(tail) {
  return /^(\d+(?:-\d+)*)bhk$/i.test(String(tail || '').trim());
}

/**
 * Avoid duplicating a trailing slug segment when BHK starts with the same token
 * (e.g. base `a2a-homeland-2` + bhk `2-2-5-3bhk` → `a2a-homeland-2-2-5-3bhk`).
 */
export function joinBaseSlugAndBhkPart(base, bhkPart) {
  if (!bhkPart) return base;

  const bhkNums = bhkPart.replace(/bhk$/i, '').split('-').filter(Boolean);
  if (!bhkNums.length) return `${base}-${bhkPart}`;

  const baseParts = base.split('-');
  const lastBase = baseParts[baseParts.length - 1];

  if (lastBase && lastBase === bhkNums[0]) {
    const rest = bhkNums.slice(1);
    if (!rest.length) return `${base}bhk`;
    return `${base}-${rest.join('-')}bhk`;
  }

  return `${base}-${bhkPart}`;
}

function joinBaseSlugAndBhkUsedOverlap(base, bhkPart) {
  if (!bhkPart) return false;
  const bhkNums = bhkPart.replace(/bhk$/i, '').split('-').filter(Boolean);
  if (!bhkNums.length) return false;
  const lastBase = base.split('-').pop();
  return Boolean(lastBase && lastBase === bhkNums[0]);
}

/** All plausible API slugs from the segment before `-flats-in-{location}`. */
export function enumerateApiSlugCandidatesFromSeoBefore(before) {
  if (!before) return [];
  if (!/bhk$/i.test(before)) {
    const norm = normalizeProjectSlugForApi(before);
    return norm ? [norm] : [];
  }

  const parts = before.split('-');
  const candidates = [];

  for (let i = parts.length - 1; i >= 1; i -= 1) {
    const tail = parts.slice(i).join('-');
    if (!isValidBhkUrlTail(tail)) continue;
    const slugPart = parts.slice(0, i).join('-');
    const norm = normalizeProjectSlugForApi(slugPart);
    if (norm) candidates.push(norm);
  }

  return [...new Set(candidates)].sort((a, b) => b.length - a.length);
}

/**
 * Build path segment after route base.
 * Returns base slug only when location is missing.
 */
export function buildProjectSeoSlugSegment({
  slug,
  propertyType = 'apartment',
  bhk = '',
  location = '',
}) {
  const base = normalizeProjectSlugForApi(slug);
  if (!base) return '';

  const loc = slugifyLocationSegment(location);
  if (!loc) return base;

  const typeWord = getPropertyTypeUrlWord(propertyType);
  const bhkPart = formatBhkForUrlSegment(bhk);
  const baseWithBhk = bhkPart ? joinBaseSlugAndBhkPart(base, bhkPart) : base;
  return `${baseWithBhk}-${typeWord}-in-${loc}`;
}

/**
 * Parse route :slug param → API slug (+ optional SEO parts).
 * @returns {{ apiSlug: string, location?: string, bhk?: string, isSeoUrl: boolean }}
 */
export function parseProjectRouteSlug(routeSlug, propertyType = 'apartment') {
  const raw = decodeRouteSlug(routeSlug);
  const normalized = normalizeProjectSlugForApi(raw);
  if (!normalized) {
    return { apiSlug: '', isSeoUrl: false };
  }

  const preferredType = getPropertyTypeUrlWord(propertyType);
  const typeWords = [
    preferredType,
    ...TYPE_WORDS.filter((w) => w !== preferredType),
  ];

  for (const typeWord of typeWords) {
    const marker = `-${typeWord}-in-`;
    const idx = normalized.lastIndexOf(marker);
    if (idx === -1) continue;

    const location = normalized.slice(idx + marker.length);
    if (!location) continue;

    const before = normalized.slice(0, idx);
    const parts = before.split('-');
    let resolved = null;
    let bestScore = [-1, -1, -1];

    for (let i = parts.length - 1; i >= 1; i -= 1) {
      const tail = parts.slice(i).join('-');
      if (!isValidBhkUrlTail(tail)) continue;
      const candidateSlug = normalizeProjectSlugForApi(parts.slice(0, i).join('-'));
      if (!candidateSlug) continue;
      if (joinBaseSlugAndBhkPart(candidateSlug, tail) !== before) continue;

      const overlap = joinBaseSlugAndBhkUsedOverlap(candidateSlug, tail) ? 1 : 0;
      const bhkSegmentCount = tail.replace(/bhk$/i, '').split('-').filter(Boolean).length;
      const score = [overlap, bhkSegmentCount, candidateSlug.length];

      const isBetter =
        score[0] > bestScore[0] ||
        (score[0] === bestScore[0] && score[1] > bestScore[1]) ||
        (score[0] === bestScore[0] && score[1] === bestScore[1] && score[2] > bestScore[2]);

      if (isBetter) {
        bestScore = score;
        resolved = {
          apiSlug: candidateSlug,
          location,
          bhk: tail.replace(/bhk$/i, ''),
          isSeoUrl: true,
        };
      }
    }

    if (resolved) return resolved;

    const longestBhkMatch = before.match(/-(\d+(?:-\d+)*)bhk$/i);
    if (longestBhkMatch) {
      const fallbackSlug = normalizeProjectSlugForApi(before.slice(0, -longestBhkMatch[0].length));
      return {
        apiSlug: fallbackSlug || normalized,
        location,
        bhk: longestBhkMatch[1],
        isSeoUrl: true,
      };
    }

    const baseSlug = normalizeProjectSlugForApi(before);
    return {
      apiSlug: baseSlug || normalized,
      location,
      bhk: '',
      isSeoUrl: true,
    };
  }

  return { apiSlug: normalized, isSeoUrl: false };
}

export function resolveSeoSlugInputsFromListingItem(item, propertyType = 'apartment') {
  if (!item || typeof item !== 'object') {
    return { slug: '', bhk: '', location: '' };
  }

  const raw = item.raw || item;
  const slug = pickProjectSlugFromItem(item) || pickProjectSlugFromItem(raw);
  const bhk = resolveBhkLabelFromItem(item, propertyType);
  const location = resolveLocationLabelFromItem(item);

  return { slug, bhk, location, propertyType };
}

export function resolveSeoSlugInputsFromProjectData(projectData, propertyType = 'apartment') {
  const proj = projectData?.project || projectData || {};
  const det = proj.details || projectData?.details || {};
  const title = String(proj.title || projectData?.title || '').trim();

  const slug =
    normalizeProjectSlugForApi(proj.slug || projectData?.slug) ||
    pickProjectSlugFromItem(proj);

  const bhk = resolveBhkFromProjectData(projectData, propertyType);

  const { locationFromProject } = resolveLocalityAndCityFromProject(projectData, title);
  let location =
    (locationFromProject && !isPlaceholder(locationFromProject)
      ? locationFromProject
      : '') ||
    resolveLocationLabelFromItem(proj) ||
    resolveLocationLabelFromItem(projectData) ||
    '';

  if (!location) {
    const detRaw = projectData?.details;
    if (Array.isArray(detRaw)) {
      location = readDetailPairValue(detRaw, 'location');
    } else if (detRaw && typeof detRaw === 'object') {
      location =
        detRaw.location ||
        detRaw.locality ||
        detRaw.area ||
        '';
    }
  }

  if (!location) {
    location =
      proj.location ||
      proj.locality ||
      projectData?.location ||
      '';
  }

  return {
    slug,
    bhk,
    location: isPlaceholder(String(location).trim()) ? '' : String(location).trim(),
    propertyType,
  };
}
